import React, { useContext, useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from "react";
import LanguageContext from "../helper/LanguageContext";
import MaterialIcon from "./MaterialIcon";
import "./EmailFolderSidebar.css";

const FOLDERS = [
  { key: "INBOX", icon: "inbox", labelKey: "email_folder_inbox" },
  { key: "SPAM", icon: "report", labelKey: "email_folder_spam" },
  { key: "DRAFT", icon: "draft", labelKey: "email_folder_drafts" },
  { key: "SENT", icon: "send", labelKey: "email_folder_sent" },
  { key: "TRASH", icon: "delete", labelKey: "email_folder_deleted" },
  { key: "ARCHIVE", icon: "archive", labelKey: "email_folder_archive" },
];

const ALL_ACCOUNTS_ID = "__ALL__";

const COLLAPSE_BREAKPOINT = 1100;
const HOVER_EXPAND_DELAY = 300;

// Overlay open/close below the breakpoint is a clip-path reveal on a fixed-width
// panel: nothing re-lays out per frame (the old width transition reflowed the list).
const PANEL_CLIP_CLOSED = "inset(0 calc(100% - 40px) 0 0)";
const PANEL_CLIP_OPEN = "inset(0 -24px 0 0)"; // leave room for the drop shadow
const PANEL_ANIM = { duration: 200, easing: "cubic-bezier(0.22, 1, 0.36, 1)" };
const prefersReducedMotion = () =>
  typeof window !== "undefined"
  && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

function isEmailAccountConnectionError(account) {
  return account?.status === "error" || account?.status === "auth_error";
}

function EmailFolderSidebar({
  emailAccounts,
  activeEmailAccountId,
  onSelectAccount,
  activeFolder,
  onSelectFolder,
  onCompose,
  onReorderAccounts,
  customFolders,
  onCreateFolder,
  onDeleteFolder,
  onAddAccount,
  onFixAccount,
}) {
  const { strings } = useContext(LanguageContext);
  const hoverTimerRef = useRef(null);
  const sidebarRef = useRef(null);

  const isWide = () => window.innerWidth >= COLLAPSE_BREAKPOINT;
  const [collapsed, setCollapsed] = useState(() => !isWide());
  const [wideScreen, setWideScreen] = useState(isWide);
  const [pinned, setPinned] = useState(false);

  const [draggedAccountId, setDraggedAccountId] = useState(null);
  const [dragOverAccountId, setDragOverAccountId] = useState(null);

  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingLoading, setCreatingLoading] = useState(false);
  const newFolderInputRef = useRef(null);

  const closingAnimRef = useRef(null);

  const cancelClosing = useCallback(() => {
    if (closingAnimRef.current) {
      closingAnimRef.current.cancel();
      closingAnimRef.current = null;
    }
  }, []);

  // Collapse the overlay panel, animating it shut first when it is open as an overlay.
  const collapse = useCallback(() => {
    const el = sidebarRef.current;
    if (closingAnimRef.current) return;
    if (isWide() || !el?.animate || prefersReducedMotion()) {
      setCollapsed(true);
      return;
    }
    const anim = el.animate(
      [{ clipPath: PANEL_CLIP_OPEN }, { clipPath: PANEL_CLIP_CLOSED }],
      { ...PANEL_ANIM, duration: 160, fill: "forwards" }
    );
    closingAnimRef.current = anim;
    anim.onfinish = () => {
      if (closingAnimRef.current === anim) setCollapsed(true);
    };
  }, []);

  // Open: reveal the panel. Closed: drop the finished closing animation once
  // the collapsed layout has been committed (no flash of the open panel).
  useLayoutEffect(() => {
    const el = sidebarRef.current;
    if (collapsed) {
      cancelClosing();
      return;
    }
    if (wideScreen || !el?.animate || prefersReducedMotion()) return;
    el.animate([{ clipPath: PANEL_CLIP_CLOSED }, { clipPath: PANEL_CLIP_OPEN }], PANEL_ANIM);
  }, [collapsed, wideScreen, cancelClosing]);

  const handleResize = useCallback(() => {
    cancelClosing();
    const wide = isWide();
    setWideScreen(wide);
    if (wide) {
      setCollapsed(false);
      setPinned(false);
    } else {
      setCollapsed(true);
      setPinned(false);
    }
  }, [cancelClosing]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  useEffect(() => {
    return () => {
      if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (creatingFolder && newFolderInputRef.current) {
      newFolderInputRef.current.focus();
    }
  }, [creatingFolder]);

  const canHoverExpand = () =>
    typeof window !== "undefined"
    && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const handleMouseEnter = useCallback(() => {
    if (isWide() || !canHoverExpand()) return;
    // Pointer came back while the panel was closing: keep it open.
    if (closingAnimRef.current) {
      cancelClosing();
      return;
    }
    if (!collapsed) return;
    hoverTimerRef.current = setTimeout(() => {
      setCollapsed(false);
    }, HOVER_EXPAND_DELAY);
  }, [collapsed, cancelClosing]);

  const handleMouseLeave = useCallback(() => {
    if (isWide() || !canHoverExpand()) return;
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    if (!pinned) collapse();
  }, [pinned, collapse]);

  const [expandedAccounts, setExpandedAccounts] = useState(() => {
    const initial = new Set();
    if (activeEmailAccountId) initial.add(activeEmailAccountId);
    return initial;
  });

  const toggleAccount = (accountId) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  };

  const handleAccountClick = (accountId) => {
    setPinned(true);
    if (activeEmailAccountId !== accountId) {
      onSelectAccount(accountId);
      setExpandedAccounts((prev) => {
        const next = new Set(prev);
        next.add(accountId);
        return next;
      });
    } else {
      toggleAccount(accountId);
    }
  };

  const collapseIfNarrow = () => {
    if (!isWide()) {
      setPinned(false);
      collapse();
    }
  };

  const handleFolderClick = (accountId, folderKey) => {
    if (activeEmailAccountId !== accountId) {
      onSelectAccount(accountId);
    }
    onSelectFolder(folderKey);
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      next.add(accountId);
      return next;
    });
    collapseIfNarrow();
  };

  const handleCreateFolderSubmit = async () => {
    const name = newFolderName.trim();
    if (!name || !onCreateFolder) return;
    setCreatingLoading(true);
    try {
      await onCreateFolder(name);
      setNewFolderName("");
      setCreatingFolder(false);
    } catch (err) {
      console.error("Failed to create folder:", err);
    }
    setCreatingLoading(false);
  };

  const handleCreateFolderKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCreateFolderSubmit();
    } else if (e.key === "Escape") {
      setCreatingFolder(false);
      setNewFolderName("");
    }
  };

  const handleDeleteFolder = (e, folder) => {
    e.stopPropagation();
    if (!onDeleteFolder) return;
    if (window.confirm(strings.email_confirm_delete_folder || "Are you sure you want to delete this folder?")) {
      onDeleteFolder(folder.id);
    }
  };

  const handleDragStart = useCallback((e, accountId) => {
    setDraggedAccountId(accountId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", accountId);
  }, []);

  const handleDragOver = useCallback((e, accountId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (accountId !== draggedAccountId) {
      setDragOverAccountId(accountId);
    }
  }, [draggedAccountId]);

  const handleDragLeave = useCallback(() => {
    setDragOverAccountId(null);
  }, []);

  const handleDrop = useCallback((e, targetAccountId) => {
    e.preventDefault();
    setDragOverAccountId(null);
    if (!draggedAccountId || draggedAccountId === targetAccountId || !onReorderAccounts) {
      setDraggedAccountId(null);
      return;
    }
    const ordered = [...emailAccounts];
    const fromIdx = ordered.findIndex((a) => (a.id || a._id) === draggedAccountId);
    const toIdx = ordered.findIndex((a) => (a.id || a._id) === targetAccountId);
    if (fromIdx === -1 || toIdx === -1) {
      setDraggedAccountId(null);
      return;
    }
    const [moved] = ordered.splice(fromIdx, 1);
    ordered.splice(toIdx, 0, moved);
    onReorderAccounts(ordered);
    setDraggedAccountId(null);
  }, [draggedAccountId, emailAccounts, onReorderAccounts]);

  const handleDragEnd = useCallback(() => {
    setDraggedAccountId(null);
    setDragOverAccountId(null);
  }, []);

  const allCustomFolders = useMemo(() => {
    if (!customFolders || customFolders.length === 0) return [];
    const seen = new Map();
    for (const f of customFolders) {
      const key = (f.name || "").toLowerCase();
      if (!seen.has(key)) seen.set(key, f);
    }
    return Array.from(seen.values());
  }, [customFolders]);

  const isAllSelected = activeEmailAccountId === ALL_ACCOUNTS_ID;
  const allExpanded = expandedAccounts.has(ALL_ACCOUNTS_ID);

  const handleAllAccountClick = () => {
    setPinned(true);
    if (!isAllSelected) {
      onSelectAccount(ALL_ACCOUNTS_ID);
      setExpandedAccounts((prev) => {
        const next = new Set(prev);
        next.add(ALL_ACCOUNTS_ID);
        return next;
      });
    } else {
      toggleAccount(ALL_ACCOUNTS_ID);
    }
  };

  const handleAllFolderClick = (folderKey) => {
    if (!isAllSelected) {
      onSelectAccount(ALL_ACCOUNTS_ID);
    }
    onSelectFolder(folderKey);
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      next.add(ALL_ACCOUNTS_ID);
      return next;
    });
    collapseIfNarrow();
  };

  if (!emailAccounts || emailAccounts.length === 0) {
    return null;
  }

  return (
    <>
    {!wideScreen && !collapsed && (
      <>
        {/* Holds the collapsed rail's 40px while the panel overlays, so the list never shifts */}
        <div className="email-folder-sidebar-spacer" aria-hidden="true" />
        <button
          type="button"
          className="email-folder-sidebar-backdrop"
          aria-label={strings.email_hide_folders || "Hide folders"}
          onClick={() => {
            setPinned(false);
            collapse();
          }}
        />
      </>
    )}
    <div
      ref={sidebarRef}
      className={`email-folder-sidebar ${collapsed ? "collapsed" : ""}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!wideScreen && (
        <button
          type="button"
          className="kj-btn kj-btn--icon kj-btn--neutral kj-btn--email kj-btn--round kj-btn--on-surface email-sidebar-collapse-btn"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (hoverTimerRef.current) {
              clearTimeout(hoverTimerRef.current);
              hoverTimerRef.current = null;
            }
            if (collapsed) {
              cancelClosing();
              setPinned(true);
              setCollapsed(false);
            } else {
              setPinned(false);
              collapse();
            }
          }}
          title={collapsed ? (strings.email_folders || "Folders") : (strings.email_hide_folders || "Hide folders")}
          aria-expanded={!collapsed}
          aria-label={collapsed ? (strings.email_folders || "Folders") : (strings.email_hide_folders || "Hide folders")}
        >
          <span
            className="material-symbols-outlined email-folder-sidebar__toggle-icon"
          >
            {collapsed ? "menu" : "chevron_left"}
          </span>
        </button>
      )}

      {!collapsed && (
        <>
          <div className="email-sidebar-accounts">
            {onAddAccount && (
              <button
                type="button"
                className="kj-btn kj-btn--primary kj-btn--email kj-btn--sm email-sidebar-add-account"
                onClick={onAddAccount}
                title={strings.email_add_account || "Add Account"}
              >
                <span
                  className="material-symbols-outlined email-sidebar-add-account-icon email-folder-sidebar__icon-18"
                >
                  add
                </span>
                <span className="email-sidebar-add-account-label">
                  {strings.email_add_account || "Add Account"}
                </span>
              </button>
            )}

            {/* "All Accounts" combined entry */}
            {emailAccounts.length > 1 && (
              <div className="email-sidebar-account email-sidebar-account--all">
                <div
                  className={`email-sidebar-account-header ${isAllSelected ? "active" : ""}`}
                  onClick={handleAllAccountClick}
                >
                  <span
                    className="material-symbols-outlined email-sidebar-expand-icon email-folder-sidebar__icon-20"
                  >
                    {allExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                  </span>
                  <MaterialIcon icon="all_inbox" size={14} className="email-sidebar-account-icon" />
                  <span className="email-sidebar-account-label">
                    {strings.email_all_accounts || "All Accounts"}
                  </span>
                </div>

                {allExpanded && (
                  <div className="email-sidebar-folders">
                    {FOLDERS.map((folder) => {
                      const isSelectedFolder = isAllSelected && activeFolder === folder.key;
                      return (
                        <div
                          key={folder.key}
                          data-testid={`folderRow-ALL-${folder.key}`}
                          className={`email-sidebar-folder ${isSelectedFolder ? "active" : ""}`}
                          onClick={() => handleAllFolderClick(folder.key)}
                        >
                          <span
                            className="material-symbols-outlined email-sidebar-folder-icon email-folder-sidebar__icon-18"
                          >
                            {folder.icon}
                          </span>
                          <span className="email-sidebar-folder-label">
                            {strings[folder.labelKey] || folder.labelKey}
                          </span>
                        </div>
                      );
                    })}

                    {allCustomFolders.length > 0 && (
                      <div className="email-sidebar-custom-divider">
                        <span className="email-sidebar-custom-divider-label">
                          {strings.email_custom_folders || "Folders"}
                        </span>
                      </div>
                    )}

                    {allCustomFolders.map((folder) => {
                      const isSelectedFolder = isAllSelected && activeFolder === folder.providerFolderId;
                      return (
                        <div
                          key={folder.id}
                          data-testid={`folderRow-ALL-CUSTOM-${folder.name}`}
                          className={`email-sidebar-folder email-sidebar-custom-folder ${isSelectedFolder ? "active" : ""}`}
                          onClick={() => handleAllFolderClick(folder.providerFolderId)}
                        >
                          <span
                            className="material-symbols-outlined email-sidebar-folder-icon email-folder-sidebar__icon-18"
                          >
                            folder
                          </span>
                          <span className="email-sidebar-folder-label">
                            {folder.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {emailAccounts.map((account) => {
              const accountId = account.id || account._id;
              const isExpanded = expandedAccounts.has(accountId);
              const isActive = activeEmailAccountId === accountId;
              const hasConnectionError = isEmailAccountConnectionError(account);

              const isDragged = draggedAccountId === accountId;
              const isDragOver = dragOverAccountId === accountId && draggedAccountId !== accountId;

              const thisAccountFolders = (customFolders || []).filter(
                (f) => String(f.emailAccount) === String(accountId)
              );

              return (
                <div
                  key={accountId}
                  className={`email-sidebar-account${isDragged ? " dragging" : ""}${isDragOver ? " drag-over" : ""}${hasConnectionError ? " email-sidebar-account--error" : ""}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, accountId)}
                  onDragOver={(e) => handleDragOver(e, accountId)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, accountId)}
                  onDragEnd={handleDragEnd}
                >
                  <div
                    className={`email-sidebar-account-header ${isActive ? "active" : ""}`}
                    onClick={() => handleAccountClick(accountId)}
                    title={hasConnectionError ? (strings.email_connection_error_hint || "Connection issue — reconnect required") : undefined}
                  >
                    <span
                      className="material-symbols-outlined email-sidebar-expand-icon email-folder-sidebar__icon-20"
                    >
                      {isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                    </span>
                    <MaterialIcon icon="drag_indicator" size={16} className="email-sidebar-drag-handle" />
                    <MaterialIcon icon="mail" size={14} className="email-sidebar-account-icon" />
                    <span className="email-sidebar-account-label">
                      {account.email || account.name || account.provider}
                    </span>
                    {hasConnectionError ? (
                      <span
                        className="material-symbols-outlined email-sidebar-account-error-icon"
                        role="button"
                        tabIndex={0}
                        title={strings.email_reconnect || "Reconnect"}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (typeof onFixAccount === "function") onFixAccount(account);
                          else if (typeof onAddAccount === "function") onAddAccount();
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            if (typeof onFixAccount === "function") onFixAccount(account);
                            else if (typeof onAddAccount === "function") onAddAccount();
                          }
                        }}
                      >
                        error
                      </span>
                    ) : null}
                  </div>

                  {isExpanded && (
                    <div className="email-sidebar-folders">
                      {FOLDERS.map((folder) => {
                        const isSelectedFolder =
                          isActive && activeFolder === folder.key;
                        return (
                          <div
                            key={folder.key}
                            data-testid={`folderRow-${folder.key}`}
                            className={`email-sidebar-folder ${isSelectedFolder ? "active" : ""}`}
                            onClick={() => handleFolderClick(accountId, folder.key)}
                          >
                            <span
                              className="material-symbols-outlined email-sidebar-folder-icon email-folder-sidebar__icon-18"
                            >
                              {folder.icon}
                            </span>
                            <span className="email-sidebar-folder-label">
                              {strings[folder.labelKey] || folder.labelKey}
                            </span>
                          </div>
                        );
                      })}

                      {thisAccountFolders.length > 0 && (
                        <div className="email-sidebar-custom-divider">
                          <span className="email-sidebar-custom-divider-label">
                            {strings.email_custom_folders || "Folders"}
                          </span>
                        </div>
                      )}

                      {thisAccountFolders.map((folder) => {
                        const isSelectedFolder =
                          isActive && activeFolder === folder.providerFolderId;
                        return (
                          <div
                            key={folder.id}
                            data-testid={`folderRow-CUSTOM-${folder.name}`}
                            className={`email-sidebar-folder email-sidebar-custom-folder ${isSelectedFolder ? "active" : ""}`}
                            onClick={() => handleFolderClick(accountId, folder.providerFolderId)}
                          >
                            <span
                              className="material-symbols-outlined email-sidebar-folder-icon email-folder-sidebar__icon-18"
                            >
                              folder
                            </span>
                            <span className="email-sidebar-folder-label">
                              {folder.name}
                            </span>
                            {onDeleteFolder && (
                              <span
                                className="email-sidebar-folder-delete"
                                onClick={(e) => handleDeleteFolder(e, folder)}
                                title={strings.email_delete_folder || "Delete Folder"}
                              >
                                <span
                                  className="material-symbols-outlined email-folder-sidebar__icon-14"
                                >
                                  close
                                </span>
                              </span>
                            )}
                          </div>
                        );
                      })}

                      {isActive && onCreateFolder && (
                        <>
                          {creatingFolder ? (
                            <div className="email-sidebar-create-folder-input">
                              <input
                                ref={newFolderInputRef}
                                data-testid="folderPickerCreateInput"
                                type="text"
                                value={newFolderName}
                                onChange={(e) => setNewFolderName(e.target.value)}
                                onKeyDown={handleCreateFolderKeyDown}
                                placeholder={strings.email_folder_name_placeholder || "Folder name"}
                                disabled={creatingLoading}
                                className="email-sidebar-folder-input"
                              />
                              <button
                                data-testid="folderPickerCreateSubmit"
                                className="kj-btn kj-btn--icon kj-btn--sm kj-btn--internal"
                                onClick={handleCreateFolderSubmit}
                                disabled={creatingLoading || !newFolderName.trim()}
                                title={strings.confirm || "Confirm"}
                              >
                                <span
                                  className="material-symbols-outlined email-folder-sidebar__icon-16"
                                >
                                  check
                                </span>
                              </button>
                              <button
                                className="kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--danger"
                                onClick={() => { setCreatingFolder(false); setNewFolderName(""); }}
                                disabled={creatingLoading}
                                title={strings.cancel || "Cancel"}
                              >
                                <span
                                  className="material-symbols-outlined email-folder-sidebar__icon-16"
                                >
                                  close
                                </span>
                              </button>
                            </div>
                          ) : (
                            <div
                              data-testid="folderPickerCreateBtn"
                              className="email-sidebar-folder email-sidebar-add-folder"
                              onClick={() => setCreatingFolder(true)}
                            >
                              <span
                                className="material-symbols-outlined email-sidebar-folder-icon email-folder-sidebar__icon-18"
                              >
                                create_new_folder
                              </span>
                              <span className="email-sidebar-folder-label">
                                {strings.email_create_folder || "New Folder"}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
    </>
  );
}

export { ALL_ACCOUNTS_ID };
export default EmailFolderSidebar;
